import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { videos } from '@/mocks/videos';

const PAGE_SIZE = 8;

const hotKeywords = ['科幻', '悬疑', '评测', '新品', '教程', '古装', '都市'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `搜索“${query}” - 苹果视频`
      : '搜索视频 - 苹果视频';
  }, [query]);

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return videos.filter((video) => {
      const haystack = [
        video.title,
        video.shortDesc,
        video.synopsis,
        video.region,
        video.year,
        video.appleCategory ?? '',
        ...video.genres,
        ...video.tags,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const paged = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const hotList = useMemo(
    () => [...videos].sort((a, b) => b.rating - a.rating).slice(0, 4),
    [],
  );

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSearchParams({ q: value });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />

      <main className="flex-1">
        <div className="bg-background-50 px-4 pb-6 pt-6 md:px-8 md:pb-8 md:pt-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            站内搜索
          </h1>

          <form
            onSubmit={submitSearch}
            className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2"
            role="search"
          >
            <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入影视名称、类型或关键词"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              aria-label="搜索关键词"
            />
            <button
              type="submit"
              className="flex-shrink-0 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
        </div>

        <div className="px-4 py-8 md:px-8 md:py-10">
          {query && (
            <p className="mb-6 text-sm text-foreground-600">
              关键词 <span className="font-bold text-primary-600">“{query}”</span> 共找到{' '}
              <span className="font-bold text-primary-600">{results.length}</span> 个结果
            </p>
          )}

          {!query ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
              <i className="ri-search-2-line text-4xl text-foreground-300" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">
                输入关键词开始搜索，试试下方热门关键词。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.map((word) => (
                  <Link
                    key={word}
                    to={`/search?q=${encodeURIComponent(word)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {word}
                  </Link>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div>
              <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-12 text-center">
                <i className="ri-emotion-unhappy-line text-4xl text-foreground-300" aria-hidden="true" />
                <p className="mt-3 text-sm text-foreground-600">
                  没有找到与“{query}”相关的内容，换个关键词试试。
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {hotKeywords.map((word) => (
                    <Link
                      key={word}
                      to={`/search?q=${encodeURIComponent(word)}`}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-900 transition-colors hover:bg-secondary-200"
                    >
                      {word}
                    </Link>
                  ))}
                </div>
              </div>

              <section className="mt-12" aria-label="热门推荐">
                <h2 className="mb-6 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  热门内容推荐
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6">
                  {hotList.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </section>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-6" data-product-shop>
                {paged.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    disabled={page === 1}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line text-lg" aria-hidden="true" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setPage(index + 1)}
                      className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                        page === index + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={page === totalPages}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line text-lg" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}